import type { ImageSlot } from '@/lib/api/amazon-listing.types'

export interface RemakeSlotImage {
  generationId: string
  assetUrl: string
  isSelected: boolean
}

export interface RemakeSlotImageGroup {
  slotNumber: number
  slotPurpose: string
  images: RemakeSlotImage[]
}

export interface RemakeAutoFillImage {
  generationId: string
  assetUrl: string
  slotNumber: number
}

/** All generations of all slots, grouped by slot (slot_number asc). Slots
 *  without usable generations are omitted. */
export function collectRemakeImageGroups(
  slots: ImageSlot[] | undefined,
): RemakeSlotImageGroup[] {
  if (!slots) return []
  const groups: RemakeSlotImageGroup[] = []
  for (const s of [...slots].sort((a, b) => a.slot_number - b.slot_number)) {
    const images: RemakeSlotImage[] = (s.generations ?? [])
      .filter((g) => !!g.asset_url)
      .map((g) => ({
        generationId: g.id,
        assetUrl: g.asset_url,
        isSelected: g.id === s.selected_generation_id,
      }))
    if (images.length === 0) continue
    groups.push({
      slotNumber: s.slot_number,
      slotPurpose: s.slot_purpose,
      images,
    })
  }
  return groups
}

/** One image per slot for Auto-Fill: selected if present, else the first
 *  generation. Slots without any usable generation are skipped. */
export function pickAutoFillImages(
  slots: ImageSlot[] | undefined,
): RemakeAutoFillImage[] {
  if (!slots) return []
  const out: RemakeAutoFillImage[] = []
  for (const s of [...slots].sort((a, b) => a.slot_number - b.slot_number)) {
    const gens = (s.generations ?? []).filter((g) => !!g.asset_url)
    if (gens.length === 0) continue
    const chosen =
      gens.find((g) => g.id === s.selected_generation_id) ?? gens[0]
    out.push({
      generationId: chosen.id,
      assetUrl: chosen.asset_url,
      slotNumber: s.slot_number,
    })
  }
  return out
}
